// One table for the event workspace (Lane 1344, UI redesign slice 1): one line
// per cell, columns that fit the width, Excel-style column filters and pages.
// The server filters, pages and lists a column's values; this component only
// keeps the buyer's choices and draws what comes back.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const BLANK = '(Blanks)';

function FilterIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 5h18l-7 8v6l-4 2v-8z" /></svg>
  );
}

// The drop-down of one column: its values with checkboxes, "Select all" and a
// search box. Nothing applies until Done. A filter is either "only these values"
// or "every value except these": unticking a value in a searched or long list
// must keep every value the list did not show (Codex UI slice 1 round 2).
function initialPick(chosen) {
  if (!chosen) return { mode: 'all', set: new Set() };
  if (Array.isArray(chosen)) return { mode: 'include', set: new Set(chosen) };
  return { mode: 'exclude', set: new Set(chosen.not || []) };
}

const PANEL_WIDTH = 260;

// Where the drop-down opens: under its header cell, kept inside the window. It is
// drawn in a layer above the page (fixed, in the page root), so no table, window
// or scroll area clips it (Ryan, slice 2b: the list was cut off in "Add parts").
function panelPlace(anchor) {
  const r = anchor.getBoundingClientRect();
  const left = Math.max(8, Math.min(r.left, window.innerWidth - PANEL_WIDTH - 8));
  const below = window.innerHeight - r.bottom - 12;
  if (below >= 260 || below >= r.top - 12) return { left, top: r.bottom + 4, maxHeight: below };
  return { left, bottom: window.innerHeight - r.top + 4, maxHeight: r.top - 12 };
}

function FilterPanel({ column, load, chosen, onApply, onClose, anchor }) {
  const [q, setQ] = useState('');
  const [facet, setFacet] = useState(null);
  const [error, setError] = useState('');
  const [pick, setPick] = useState(() => initialPick(chosen));
  const [place, setPlace] = useState(null);
  const ref = useRef(null);
  useLayoutEffect(() => { if (anchor) setPlace(panelPlace(anchor)); }, [anchor]);

  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      load(column, q).then((f) => { if (live) { setFacet(f); setError(''); } })
        .catch((e) => { if (live) setError(e.message); });
    }, q ? 250 : 0);
    return () => { live = false; clearTimeout(t); };
  }, [column, q, load]);
  useEffect(() => { setPick(initialPick(chosen)); }, [chosen]);
  useEffect(() => {
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    // The page or a table scrolled under it: close rather than float away.
    const moved = (e) => { if (!(ref.current && e.target instanceof Node && ref.current.contains(e.target))) onClose(); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    window.addEventListener('scroll', moved, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc);
      window.removeEventListener('scroll', moved, true); window.removeEventListener('resize', onClose);
    };
  }, [onClose]);

  const values = facet ? facet.values.map((v) => v.value) : [];
  const isOn = (v) => (pick.mode === 'all' ? true : pick.mode === 'include' ? pick.set.has(v) : !pick.set.has(v));
  const allOn = values.length > 0 && values.every(isOn);
  const setValues = (list, on) => {
    const mode = pick.mode === 'all' ? 'exclude' : pick.mode;
    const next = new Set(pick.mode === 'all' ? [] : pick.set);
    // include: the set holds the ticked values; exclude: the unticked ones.
    list.forEach((v) => { if ((mode === 'include') === on) next.add(v); else next.delete(v); });
    setPick({ mode, set: next });
  };
  const toggle = (v) => setValues([v], !isOn(v));
  // Without a search, "Select all" means every value of the column, listed or not:
  // ticked is no filter, unticked is none (only what is ticked next). Unticking only
  // the listed values kept every value the list did not show (Ryan, slice 2b).
  // With a search, "Select all shown" changes only the values shown.
  const toggleAll = () => {
    if (q) setValues(values, !allOn);
    else setPick(allOn ? { mode: 'include', set: new Set() } : { mode: 'all', set: new Set() });
  };
  const result = () => {
    if (pick.mode === 'all' || (pick.mode === 'exclude' && pick.set.size === 0)) return null;
    if (pick.mode === 'exclude') return { not: [...pick.set] };
    return [...pick.set];
  };
  const empty = pick.mode === 'include' && pick.set.size === 0;
  if (!anchor || !place) return null;
  const root = anchor.closest('.ll') || document.body;
  return createPortal(
    <div className="srce-fpanel" ref={ref} role="dialog" aria-label="Filter"
      style={{ left: place.left, top: place.top, bottom: place.bottom, maxHeight: Math.max(160, place.maxHeight) }}>
      <input className="srce-input srce-fsearch" placeholder="Search values" value={q} autoFocus
        aria-label="Search values" onChange={(e) => setQ(e.target.value)} />
      {error && <div className="srce-error">{error}</div>}
      {!facet && !error && <div className="srce-mut">Loading…</div>}
      {facet && (
        <div className="srce-flist">
          <label className="srce-fitem srce-strong">
            <input type="checkbox" checked={allOn} onChange={toggleAll} />{q ? 'Select all shown' : 'Select all'}
          </label>
          {facet.values.map((v) => (
            <label key={v.value} className="srce-fitem" title={v.value}>
              <input type="checkbox" checked={isOn(v.value)} onChange={() => toggle(v.value)} />
              <span className={`srce-ell${v.value === BLANK ? ' srce-mut' : ''}`}>{v.value}</span>
              <span className="srce-push srce-mut">{v.count.toLocaleString()}</span>
            </label>
          ))}
          {facet.more > 0 && <div className="srce-mut srce-fitem">{facet.more.toLocaleString()} more: search to find them.</div>}
        </div>
      )}
      <div className="srce-row">
        <button type="button" className="srce-btn srce-btn--sm" onClick={onClose}>Cancel</button>
        <button type="button" className="srce-btn srce-btn--primary srce-btn--sm srce-push" disabled={empty}
          onClick={() => onApply(result())}>Done</button>
      </div>
    </div>,
    root,
  );
}

// columns: [{ key, label, weight, num, filter (server column), render(row), title(row),
//   mark (an element after the header label, e.g. a supplier history mark) }]
export default function WorkspaceTable({
  fetchPage, columns, rowKey, refreshKey, onRowClick, selectable, selected, onSelect,
  onData, emptyText, ariaLabel, filters: outerFilters, onFiltersChange,
  // selectKey: what a tick selects (default the row): the rows of one split line share
  // it, so a tick always takes the whole line (slice 2b). rowClass: a row's own style.
  selectKey, rowClass,
  // expandedKey / renderExpanded (Lane 1395 QA): the row with this key shows a full-width
  // panel under it. The row keeps its role; the caller puts a disclosure button in a cell
  // (Codex plan r1 #5).
  expandedKey, renderExpanded,
  // resetKey: a change of what the table shows (the Bids round) starts again at page 1.
  resetKey,
}) {
  const pickKey = selectKey || rowKey;
  const [innerFilters, setInnerFilters] = useState({});
  const filters = outerFilters || innerFilters;
  const setFilters = onFiltersChange || setInnerFilters;
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);
  const [anchor, setAnchor] = useState(null);
  const dataRef = useRef(onData);
  dataRef.current = onData;

  const filterParam = Object.keys(filters).length ? JSON.stringify(filters) : undefined;
  useEffect(() => { setPage(1); }, [filterParam, resetKey]);
  useEffect(() => {
    let live = true;
    fetchPage({ filters: filterParam, page })
      .then((d) => {
        if (!live) return;
        // A page past the end (the rows changed under it): go to the last page.
        if (d && d.total_pages > 0 && page > d.total_pages) { setPage(d.total_pages); return; }
        setData(d); setError(''); if (dataRef.current) dataRef.current(d);
      })
      .catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [fetchPage, filterParam, page, refreshKey]);

  const loadFacet = useCallback((column, q) => fetchPage({ filters: filterParam, page: 1, facet: column,
    facet_q: q || undefined }).then((d) => d.facet), [fetchPage, filterParam]);

  // Each column keeps a readable minimum width (its own `min`, else by kind); when the
  // columns need more than the width, the table scrolls sideways inside itself, never
  // the page (Ryan, slice 2b: Offers with every column on squeezed to "+14....").
  const mins = columns.map((c) => c.min || (c.num ? 88 : 120));
  const template = columns.map((c, i) => `minmax(${mins[i]}px, ${c.weight || 1}fr)`).join(' ');
  const minWidth = mins.reduce((a, b) => a + b, 0) + (selectable ? 32 : 0);
  const grid = { gridTemplateColumns: selectable ? `32px ${template}` : template, minWidth };
  // The first column (the part or name) stays in place when the table scrolls sideways.
  const stickStyle = { '--stick-left': selectable ? '32px' : '0px' };
  // Sideways scroll only when the columns do not fit: a scrolling box would stop the
  // header row from staying at the top of the page.
  const boxRef = useRef(null);
  const [wide, setWide] = useState(false);
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return undefined;
    const check = () => setWide(box.clientWidth > 0 && minWidth > box.clientWidth);
    check();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(check);
    ro.observe(box);
    return () => ro.disconnect();
  }, [minWidth]);
  const items = data ? data.items : [];
  const pageKeys = [...new Set(items.map(pickKey))];
  const allPicked = selectable && pageKeys.length > 0 && pageKeys.every((k) => selected.has(k));
  return (
    <div className={`srce-wt${wide ? ' srce-wt--wide' : ''}`} ref={boxRef} aria-label={ariaLabel}>
      {error && <div className="srce-error" role="alert">{error}</div>}
      <div className="srce-wt-row srce-wt-head" style={grid} role="row">
        {selectable && (
          <div className="srce-wt-cell srce-wt-check srce-wt-stick" style={{ '--stick-left': '0px' }}>
            <input type="checkbox" aria-label="Select all rows on this page" checked={allPicked}
              onChange={() => onSelect(pageKeys, !allPicked, items)} />
          </div>
        )}
        {columns.map((c, ci) => (
          <div key={c.key} className={`srce-wt-cell${c.num ? ' srce-wt-num' : ''}${c.filter ? ' srce-wt-fcol' : ''}${c.mark ? ' srce-wt-mcol' : ''}${ci === 0 ? ' srce-wt-stick' : ''}`}
            style={ci === 0 ? stickStyle : undefined} role="columnheader">
            <span className="srce-ell" title={c.fullLabel || c.label}>{c.label}</span>
            {c.mark}
            {c.filter && (
              <button type="button" aria-label={`Filter ${c.label}`}
                className={`srce-fbtn${filters[c.filter] ? ' srce-fbtn--on' : ''}`}
                onClick={(e) => {
                  const cell = e.currentTarget.closest('[role="columnheader"]');
                  setAnchor(cell);
                  setOpen(open === c.filter ? null : c.filter);
                }}><FilterIcon /></button>
            )}
            {c.filter && open === c.filter && (
              <FilterPanel column={c.filter} load={loadFacet} chosen={filters[c.filter] || null} anchor={anchor}
                onClose={() => setOpen(null)}
                onApply={(values) => {
                  const next = { ...filters };
                  if (values === null) delete next[c.filter]; else next[c.filter] = values;
                  setFilters(next);
                  setOpen(null);
                }} />
            )}
          </div>
        ))}
      </div>
      {!data && !error && <div className="ll-loading">Loading…</div>}
      {data && items.length === 0 && <div className="srce-mut srce-wt-empty">{emptyText || 'Nothing here.'}</div>}
      {items.map((r) => {
        const k = rowKey(r);
        const pk = pickKey(r);
        return (
          <React.Fragment key={k}>
          <div role="row" style={grid}
            className={`srce-wt-row${onRowClick ? ' srce-wt-click' : ''}${selectable && selected.has(pk) ? ' srce-wt-sel' : ''}${rowClass ? ` ${rowClass(r) || ''}` : ''}${renderExpanded && expandedKey === k ? ' srce-wt-open' : ''}`}
            onClick={onRowClick ? () => onRowClick(r) : undefined}>
            {selectable && (
              <div className="srce-wt-cell srce-wt-check srce-wt-stick" style={{ '--stick-left': '0px' }} onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" aria-label="Select row" checked={selected.has(pk)}
                  onChange={() => onSelect([pk], !selected.has(pk), items.filter((x) => pickKey(x) === pk))} />
              </div>
            )}
            {columns.map((c, ci) => {
              const content = c.render ? c.render(r) : r[c.key];
              const title = c.title ? c.title(r) : (typeof content === 'string' ? content : undefined);
              return (
                <div key={c.key} className={`srce-wt-cell${c.num ? ' srce-wt-num' : ''}${c.cls ? ` ${c.cls(r)}` : ''}${ci === 0 ? ' srce-wt-stick' : ''}`}
                  style={ci === 0 ? stickStyle : undefined} title={title}>
                  {content === null || content === undefined || content === '' ? <span className="srce-mut">-</span> : content}
                </div>
              );
            })}
          </div>
          {renderExpanded && expandedKey === k && <div className="srce-wt-exp" id={`wt-exp-${k}`}>{renderExpanded(r)}</div>}
          </React.Fragment>
        );
      })}
      {data && data.total_pages > 1 && (
        <div className="srce-row srce-wt-pages">
          <button type="button" className="srce-btn srce-btn--sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
          <span className="srce-mut">Page {page} of {data.total_pages}</span>
          <button type="button" className="srce-btn srce-btn--sm" disabled={page >= data.total_pages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
